import React, { useRef, useState } from "react";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ImagePlus, Trash2, Loader2 } from "lucide-react";

/**
 * PhotoUploader — uploads via /api/uploads and returns list of {id, storage_path}.
 * Photos are shown via /api/uploads/{id} with cookie auth.
 * Props: value (array of {id, ...}), onChange(next).
 */
export default function PhotoUploader({ value = [], onChange, max = 8, testid = "photo-uploader" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const openPicker = () => inputRef.current?.click();

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (value.length + files.length > max) {
      toast.error(`Maksimum ${max} foto.`);
      return;
    }
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        try {
          const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
          uploaded.push(data);
        } catch (err) {
          toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
      }
      if (uploaded.length) onChange([...(value || []), ...uploaded]);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (id) => onChange(value.filter((f) => f.id !== id));

  return (
    <div className="space-y-3" data-testid={testid}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFiles}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {value.map((f) => (
          <div key={f.id} className="relative aspect-square rounded-md overflow-hidden border border-border/60 group">
            <img
              src={`${API}/uploads/${f.id}`}
              alt={f.original_filename || "foto"}
              className="h-full w-full object-cover"
              onError={(e) => { e.currentTarget.style.opacity = 0.4; }}
            />
            <button
              type="button"
              onClick={() => remove(f.id)}
              className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/90 border border-border grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`${testid}-remove-${f.id}`}
              aria-label="Hapus foto"
            >
              <Trash2 className="h-3 w-3 text-destructive" />
            </button>
          </div>
        ))}
        {value.length < max && (
          <button
            type="button"
            onClick={openPicker}
            disabled={busy}
            className="aspect-square rounded-md border-2 border-dashed border-border/70 hover:border-primary/60 transition-colors flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-primary"
            data-testid={`${testid}-add`}
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
            <span className="text-[11px]">{busy ? "Upload..." : "Tambah"}</span>
          </button>
        )}
      </div>
      <div className="text-[11px] text-muted-foreground">
        Maks {max} foto · JPG/PNG/WebP · ≤ 8MB per file.
      </div>
    </div>
  );
}
